import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { Save, Eye, EyeOff, ChevronDown, ChevronUp, Upload, Search, X } from 'lucide-react';
import { REFINE_BY_TAGS } from '../lib/constants';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminStockPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingStock, setEditingStock] = useState({});
  const [expandedProducts, setExpandedProducts] = useState({});
  const [search, setSearch] = useState('');
  const [productType, setProductType] = useState('');
  const [tag, setTag] = useState('');

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    if (user.role !== 'admin') { navigate('/'); toast.error('Admin access required'); return; }
    fetchProducts();
  }, [user, authLoading, navigate]);

  const fetchProducts = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/products`, { withCredentials: true });
      setProducts(data.products);
      const stockMap = {};
      data.products.forEach(p => {
        stockMap[p.id] = { main: p.stock || 0 };
        if (p.variants) { p.variants.forEach(v => { stockMap[p.id][v.label] = v.stock ?? 0; }); }
      });
      setEditingStock(stockMap);
    } catch { toast.error('Failed to load products'); }
    finally { setLoading(false); }
  };

  const handleUpdateStock = async (productId) => {
    try {
      await axios.patch(`${API}/admin/products/${productId}/stock`, { stock: parseInt(editingStock[productId]?.main) || 0 }, { withCredentials: true });
      toast.success('Stock updated'); fetchProducts();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleUpdateVariantStock = async (productId, variantLabel) => {
    try {
      await axios.patch(`${API}/admin/products/${productId}/variant-stock`, { variant_label: variantLabel, stock: parseInt(editingStock[productId]?.[variantLabel]) || 0 }, { withCredentials: true });
      toast.success(`${variantLabel} stock updated`); fetchProducts();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleToggleVisibility = async (productId, nextVisible) => {
    try {
      await axios.patch(`${API}/admin/products/${productId}/visibility`, { visible: nextVisible }, { withCredentials: true });
      toast.success(nextVisible ? 'Listing shown' : 'Listing hidden');
      fetchProducts();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleToggleVariantVisibility = async (productId, variantLabel, nextVisible) => {
    try {
      await axios.patch(`${API}/admin/products/${productId}/variant-visibility`, { variant_label: variantLabel, visible: nextVisible }, { withCredentials: true });
      toast.success(`Variant ${nextVisible ? 'shown' : 'hidden'}`);
      fetchProducts();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleImageUpload = async (productId, file) => {
    if (!file) return;
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/admin/products/${productId}/image/upload`, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Product image updated'); fetchProducts();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to upload image'); }
  };

  const toggleExpand = (id) => setExpandedProducts(prev => ({ ...prev, [id]: !prev[id] }));

  const filteredProducts = useMemo(() => {
    return products.filter(p => {
      if (search) {
        const q = search.toLowerCase();
        const match = p.name?.toLowerCase().includes(q) || p.description?.toLowerCase().includes(q) || p.cas_number?.toLowerCase().includes(q);
        if (!match) return false;
      }
      if (productType && !(p.product_types || []).includes(productType)) return false;
      if (tag && !(p.tags || []).includes(tag)) return false;
      return true;
    });
  }, [products, search, productType, tag]);

  const clearFilters = () => { setSearch(''); setProductType(''); setTag(''); };
  const hasActiveFilters = search || productType || tag;

  if (loading) return <div className="max-w-4xl mx-auto px-4 py-12">{[...Array(5)].map((_, i) => <div key={i} className="h-16 bg-muted animate-pulse rounded mb-3" />)}</div>;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12" data-testid="admin-stock-page">
      <div className="mb-8">
        <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[hsl(210,45%,40%)] mb-2 font-body">Admin</p>
        <h1 className="text-4xl tracking-tight font-light text-foreground font-heading">Manage Inventory</h1>
        <p className="mt-2 text-base text-muted-foreground font-body">Update stock, manage variant quantities, and show/hide listings.</p>
      </div>

      {/* Filters */}
      <div className="mb-6 space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search products..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" data-testid="admin-stock-search" />
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mr-1">Product Type:</span>
          <Badge variant={!productType ? 'default' : 'outline'} className="cursor-pointer" onClick={() => setProductType('')} data-testid="admin-filter-type-all">All</Badge>
          <Badge variant={productType === 'peptide' ? 'default' : 'outline'} className="cursor-pointer" onClick={() => setProductType('peptide')} data-testid="admin-filter-type-peptides">Peptides</Badge>
          <Badge variant={productType === 'equipment' ? 'default' : 'outline'} className="cursor-pointer" onClick={() => setProductType('equipment')} data-testid="admin-filter-type-equipment">Equipment</Badge>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mr-1">Refine by:</span>
          {REFINE_BY_TAGS.map(t => (
            <Badge
              key={t}
              variant={tag === t ? 'default' : 'outline'}
              className="cursor-pointer font-normal text-xs"
              onClick={() => setTag(tag === t ? '' : t)}
              data-testid={`admin-filter-tag-${t.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}`}
            >
              {t}
            </Badge>
          ))}
          {hasActiveFilters && (
            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={clearFilters} data-testid="admin-clear-filters">
              <X className="h-3 w-3 mr-1" /> Clear
            </Button>
          )}
        </div>
        <p className="text-xs text-muted-foreground font-body">Showing {filteredProducts.length} of {products.length} products</p>
      </div>

      <div className="space-y-3">
        {filteredProducts.map(product => (
          <Card key={product.id} className={`${product.visible === false ? 'opacity-60' : ''}`} data-testid={`admin-product-${product.id}`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <label
                  className="relative w-12 h-12 rounded-md bg-secondary/20 overflow-hidden flex-shrink-0 cursor-pointer group"
                  data-testid={`image-upload-label-${product.id}`}
                  title="Click to upload new image"
                >
                  <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <Upload className="h-4 w-4 text-white" />
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    className="sr-only"
                    onChange={(e) => handleImageUpload(product.id, e.target.files?.[0])}
                    data-testid={`image-upload-${product.id}`}
                  />
                </label>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-foreground truncate">{product.name}</p>
                    {product.visible === false && <Badge variant="outline" className="text-xs text-red-500 border-red-200">Hidden</Badge>}
                  </div>
                  <div className="flex gap-1 mt-0.5">{product.product_types?.map(t => (<Badge key={t} variant="outline" className="text-xs">{t}</Badge>))}</div>
                </div>
                <div
                  className="flex items-center gap-2 cursor-pointer select-none px-2 py-1 rounded hover:bg-muted/40 transition-colors"
                  onClick={() => handleToggleVisibility(product.id, product.visible === false)}
                  data-testid={`visibility-toggle-row-${product.id}`}
                  role="button"
                  tabIndex={0}
                >
                  {product.visible === false ? <EyeOff className="h-4 w-4 text-red-500" /> : <Eye className="h-4 w-4 text-green-600" />}
                  <Switch
                    checked={product.visible !== false}
                    onCheckedChange={(checked) => handleToggleVisibility(product.id, checked)}
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`visibility-toggle-${product.id}`}
                  />
                  <span className="text-xs text-muted-foreground w-10">{product.visible === false ? 'Hide' : 'Show'}</span>
                </div>
                {product.variants && product.variants.length > 0 ? (
                  <Button variant="ghost" size="sm" onClick={() => toggleExpand(product.id)} data-testid={`expand-${product.id}`}>
                    {expandedProducts[product.id] ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </Button>
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">Stock:</span>
                    <Input type="number" min="0" className="w-20 h-9 text-center" value={editingStock[product.id]?.main ?? 0}
                      onChange={(e) => setEditingStock(prev => ({ ...prev, [product.id]: { ...prev[product.id], main: e.target.value } }))} data-testid={`stock-input-${product.id}`} />
                    <Button size="sm" onClick={() => handleUpdateStock(product.id)} className="bg-[hsl(210,45%,40%)] hover:bg-[hsl(210,45%,30%)] text-white" data-testid={`save-stock-${product.id}`}><Save className="h-3.5 w-3.5" /></Button>
                  </div>
                )}
              </div>
              {product.variants && product.variants.length > 0 && expandedProducts[product.id] && (
                <div className="mt-4 pt-4 border-t border-border space-y-2">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Variant Stock</p>
                  {product.variants.map(v => (
                    <div key={v.label} className={`flex items-center gap-3 pl-16 ${v.visible === false ? 'opacity-50' : ''}`}>
                      <span className="text-sm text-foreground w-20">{v.label}</span>
                      <span className="text-xs text-muted-foreground">${v.price.toFixed(2)}</span>
                      <div className="flex items-center gap-1 ml-2">
                        <Switch checked={v.visible !== false} onCheckedChange={(checked) => handleToggleVariantVisibility(product.id, v.label, checked)}
                          data-testid={`variant-vis-${product.id}-${v.label}`} className="scale-75" />
                        <span className="text-xs text-muted-foreground">{v.visible === false ? 'Hidden' : 'Shown'}</span>
                      </div>
                      <div className="flex items-center gap-2 ml-auto">
                        <span className="text-xs text-muted-foreground">Stock:</span>
                        <Input type="number" min="0" className="w-20 h-8 text-center text-sm" value={editingStock[product.id]?.[v.label] ?? 0}
                          onChange={(e) => setEditingStock(prev => ({ ...prev, [product.id]: { ...prev[product.id], [v.label]: e.target.value } }))} data-testid={`variant-stock-${product.id}-${v.label}`} />
                        <Button size="sm" className="h-8 bg-[hsl(210,45%,40%)] hover:bg-[hsl(210,45%,30%)] text-white" onClick={() => handleUpdateVariantStock(product.id, v.label)} data-testid={`save-variant-${product.id}-${v.label}`}><Save className="h-3 w-3" /></Button>
                      </div>
                    </div>
                  ))}
                  <p className="text-xs text-muted-foreground pl-16 pt-1">Total stock: {product.variants.reduce((s, v) => s + (v.stock || 0), 0)}</p>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
